import React, { useState, useEffect } from 'react';
import { X, Download, Share, Plus } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detect device type
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    const isAndroidDevice = /android/.test(userAgent);
    
    // Check if already installed (standalone mode)
    const isInStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone === true;

    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);
    setIsStandalone(isInStandalone);

    // Don't show if already installed
    if (isInStandalone) {
      return;
    }

    // Check if user has already dismissed the prompt
    const hasBeenDismissed = localStorage.getItem('pwa-install-dismissed');
    if (hasBeenDismissed) {
      return;
    }

    // For iOS - show custom prompt immediately
    if (isIOSDevice) {
      // Show after a short delay to let page load
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    }

    // For Android/Chrome - listen for beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      // For iOS, we can't trigger install programmatically
      // The prompt shows instructions
      return;
    }

    if (deferredPrompt) {
      // For Android/Chrome
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Remember dismissal for 7 days
    const dismissedUntil = Date.now() + (7 * 24 * 60 * 60 * 1000);
    localStorage.setItem('pwa-install-dismissed', dismissedUntil.toString());
  };

  const handleLaterClick = () => {
    setShowPrompt(false);
    // Show again in 24 hours
    const showAgainAt = Date.now() + (24 * 60 * 60 * 1000);
    localStorage.setItem('pwa-install-dismissed', showAgainAt.toString());
  };

  if (!showPrompt || isStandalone) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-end md:items-center justify-center p-4">
      <div className="bg-white rounded-t-3xl md:rounded-2xl shadow-2xl w-full md:max-w-md transform transition-transform duration-300 animate-slide-up">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6 rounded-t-3xl md:rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center">
                <img 
                  src="/faviicon2.ico" 
                  alt="Quick Runner" 
                  className="w-8 h-8"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-8 h-8 bg-orange-500 rounded-lg hidden items-center justify-center">
                  <span className="text-white font-bold text-sm">QR</span>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-bold">Quick Runner</h3>
                <p className="text-orange-100 text-sm">Install our app</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-white hover:text-orange-200 p-2 rounded-full hover:bg-white/10 transition-colors"
              aria-label="Close install prompt"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {isIOS ? (
            // iOS Instructions
            <div className="space-y-4">
              <div className="text-center">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Add to Home Screen
                </h4>
                <p className="text-gray-600 text-sm mb-4">
                  Get quick access to Quick Runner right from your home screen
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">1</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Tap the</span>
                    <Share className="w-5 h-5 text-blue-600" />
                    <span className="text-sm text-gray-700">share button</span>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">2</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Select</span>
                    <div className="flex items-center space-x-1 px-2 py-1 bg-white rounded border">
                      <Plus className="w-4 h-4 text-blue-600" />
                      <span className="text-xs text-gray-700">Add to Home Screen</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-bold">3</span>
                  </div>
                  <span className="text-sm text-gray-700">Tap "Add" to confirm</span>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={handleLaterClick}
                  className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                >
                  Maybe Later
                </button>
                <button
                  onClick={handleDismiss}
                  className="flex-1 py-3 px-4 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 transition-colors"
                >
                  Got It
                </button>
              </div>
            </div>
          ) : (
            // Android/Chrome Install
            <div className="space-y-4">
              <div className="text-center">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  {isAndroid ? "Add to Home Screen" : "Install Quick Runner App"}
                </h4>
                <p className="text-gray-600 text-sm mb-4">
                  {isAndroid
                    ? "Get quick access to Quick Runner right from your home screen"
                    : "Get the full app experience with offline access and faster loading"
                  }
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg">
                  <Download className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Works offline</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg">
                  <Download className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Faster loading</span>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg">
                  <Download className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Home screen access</span>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={handleLaterClick}
                  className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                >
                  Maybe Later
                </button>
                <button
                  onClick={handleInstallClick}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-xl font-semibold hover:from-orange-700 hover:to-amber-700 transition-all flex items-center justify-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Install App</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
